import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type maplibregl from 'maplibre-gl'
import { MapPin } from 'lucide-react'

import { ClusterMarkers, type ClusterPoint } from 'features/Map/components/ClusterMarkers'
import { LocationMarker } from 'features/Map/components/LocationMarker'
import { MapContainer } from 'features/Map/components/MapContainer'
import { OtherRetailersLayer } from 'features/Map/components/OtherRetailersLayer'
import { TradeAreaRings } from 'features/Map/components/TradeAreaRings'
import { MAP_COLORS, SITE_ZOOM, siteColorForIndex } from 'features/Map/config/mapConfig'
import { tradeAreaService } from 'features/Map/services/tradeAreaService'
import { cn } from 'lib/utils'

import { asArray, asNumber, asText } from '../payload'
import { useReportReady } from '../readiness'
import { useBlockRenderMode } from '../renderMode'

/**
 * Map blocks (MYD-500): `location.map`, `portfolio.map`, `evaluation.sitesMap`.
 * Payload shapes mirror backend/presentations/providers/mapElements.js.
 *
 * On a slide the map is STATIC: MapLibre's handlers are off, there are no
 * controls, and markers ignore the pointer (they are DOM elements with popups,
 * so a tap would open one instead of turning the page). Every click passes
 * through to the presenter, which advances.
 *
 * The same components draw the interactive map in "Frame this view"
 * (FrameMapDialog), passed `interactive` and `onCameraChange`, so what the
 * author frames is exactly what the slide shows.
 */

export interface MapCamera {
	lng: number
	lat: number
	zoom: number
	bearing: number
	pitch: number
}

export interface MapViewProps {
	payload: any
	/** For "Frame this view" only. A slide map is never interactive. */
	interactive?: boolean
	onCameraChange?: (camera: MapCamera) => void
}

type Basemap = 'street' | 'satellite'

/** A usable camera from a payload, or null (fit to the content instead). */
export const cameraFrom = (value: unknown): MapCamera | null => {
	const camera = value as Partial<MapCamera> | null
	const lng = asNumber(camera?.lng)
	const lat = asNumber(camera?.lat)
	const zoom = asNumber(camera?.zoom)
	if (lng === null || lat === null || zoom === null) return null
	return { lng, lat, zoom, bearing: asNumber(camera?.bearing) ?? 0, pitch: asNumber(camera?.pitch) ?? 0 }
}

/** The camera a map is showing, rounded to what is worth storing. */
export const readCamera = (map: maplibregl.Map): MapCamera => {
	const center = map.getCenter()
	const round = (value: number, places: number) => Math.round(value * 10 ** places) / 10 ** places
	return {
		lng: round(center.lng, 6),
		lat: round(center.lat, 6),
		zoom: round(map.getZoom(), 2),
		bearing: round(map.getBearing(), 1),
		pitch: round(map.getPitch(), 1),
	}
}

const isPoint = (lat: unknown, lng: unknown) => asNumber(lat) !== null && asNumber(lng) !== null

// =============================================================================
// The shared static map
// =============================================================================

/** The still stand-in drawn where a live map would cost a WebGL context. */
function MapStandIn({ label }: { label: string }) {
	return (
		<div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 rounded-xl bg-[#dfe8ee] text-[#5b7486]" data-testid="map-stand-in">
			<MapPin className="h-10 w-10" />
			{label && <span className="text-[18px] font-medium">{label}</span>}
		</div>
	)
}

interface SlideMapProps extends Omit<MapViewProps, 'payload'> {
	camera: MapCamera | null
	basemap: Basemap
	/** What to fit when there is no camera: bounds, or one point to centre on. */
	fit: { bounds: maplibregl.LngLatBoundsLike } | { center: [number, number]; zoom: number } | null
	/** Nothing to show at all: said plainly instead of an empty map. */
	empty?: string
	/** Shown in the stand-in, e.g. the location's name. */
	label: string
	/** Drawn over the map: a legend, a count of what could not be placed. */
	overlay?: ReactNode
	children: (map: maplibregl.Map) => ReactNode
}

function SlideMap({ camera, basemap, fit, empty, label, overlay, children, interactive = false, onCameraChange }: SlideMapProps) {
	const mode = useBlockRenderMode()
	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const [ready, setReady] = useState(false)
	// Tell the presenter's Start button when the tiles are in (MYD-515). A no-op
	// anywhere nobody is waiting.
	useReportReady(ready)
	const onCameraRef = useRef(onCameraChange)
	onCameraRef.current = onCameraChange

	// One key per framing, so the effect below re-frames only when the framing
	// actually changes, not on every render of the payload.
	const cameraKey = camera ? `${camera.lng},${camera.lat},${camera.zoom},${camera.bearing},${camera.pitch}` : 'fit'
	const fitKey = JSON.stringify(fit)

	useEffect(() => {
		if (!map) return
		setReady(false)
		const frame = () => {
			if (camera) map.jumpTo({ center: [camera.lng, camera.lat], zoom: camera.zoom, bearing: camera.bearing, pitch: camera.pitch })
			else if (fit && 'bounds' in fit) map.fitBounds(fit.bounds, { padding: 48, duration: 0, maxZoom: 13 })
			else if (fit && 'center' in fit) map.jumpTo({ center: fit.center, zoom: fit.zoom })
		}
		frame()
		// With no stored camera, a new region size (a layout change, the dialog
		// opening) must refit, or the content drifts out of frame.
		const refit = () => {
			if (!camera) frame()
		}
		const markReady = () => setReady(true)
		map.on('resize', refit)
		map.once('idle', markReady)
		return () => {
			map.off('resize', refit)
			map.off('idle', markReady)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, cameraKey, fitKey])

	useEffect(() => {
		if (!map || !interactive) return
		const report = () => onCameraRef.current?.(readCamera(map))
		map.on('moveend', report)
		return () => {
			map.off('moveend', report)
		}
	}, [map, interactive])

	if (empty) {
		return (
			<div className="flex h-full min-h-[120px] items-center justify-center rounded-xl bg-gray-50 px-6 text-center text-[18px] text-gray-500">{empty}</div>
		)
	}
	if (mode === 'thumbnail') return <MapStandIn label={label} />

	return (
		<div
			className={cn(
				'relative h-full min-h-[200px] overflow-hidden rounded-xl bg-[#e5e3df]',
				// Markers are DOM elements with click handlers and popups. On a slide
				// they must let the click through, so it turns the page.
				!interactive && '[&_.maplibregl-marker]:pointer-events-none',
			)}
			data-map-ready={ready ? 'true' : 'false'}
		>
			<MapContainer
				className="!rounded-none"
				interactive={interactive}
				showControls={interactive}
				// A capture is stored, and satellite imagery is never stored (MYD-508).
				fixedBasemap={mode === 'capture' ? 'street' : basemap}
				preserveDrawingBuffer={mode === 'capture'}
				center={camera ? [camera.lng, camera.lat] : undefined}
				zoom={camera?.zoom}
				bearing={camera?.bearing}
				pitch={camera?.pitch}
				onMapLoad={setMap}
			>
				{map && children(map)}
			</MapContainer>
			{overlay}
			{/* Tiles arrive over a second or so. A veil rather than a spinner:
			    the pre-mounted next slide usually has them cached already. */}
			<div
				className={cn('pointer-events-none absolute inset-0 bg-[#e5e3df] transition-opacity duration-300', ready ? 'opacity-0' : 'opacity-60')}
				aria-hidden
			/>
		</div>
	)
}

const basemapOf = (payload: any): Basemap => (payload?.basemap === 'satellite' ? 'satellite' : 'street')

// =============================================================================
// location.map
// =============================================================================

export function LocationMapBlock({ payload, interactive, onCameraChange }: MapViewProps) {
	const site = payload?.site && isPoint(payload.site.lat, payload.site.lng) ? payload.site : null
	const rings = asArray<number>(payload?.rings).filter(ring => typeof ring === 'number' && ring > 0)
	const retailers = asArray<any>(payload?.retailers).filter(retailer => isPoint(retailer?.lat, retailer?.lng))
	const ringsKey = rings.join(',')
	const retailersKey = retailers.map(retailer => asText(retailer.id)).join(',')

	const fit = useMemo(() => {
		if (!site) return null
		if (rings.length > 0) return { bounds: tradeAreaService.getBoundsForSitesWithRings([{ longitude: site.lng, latitude: site.lat }], rings) }
		if (retailers.length > 0) {
			return { bounds: tradeAreaService.getBoundsForSites([{ longitude: site.lng, latitude: site.lat }, ...retailers.map(r => ({ longitude: r.lng, latitude: r.lat }))]) }
		}
		return { center: [site.lng, site.lat] as [number, number], zoom: SITE_ZOOM }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [site?.lat, site?.lng, ringsKey, retailersKey])

	const siteId = asText(site?.id) || 'site'
	const layerRetailers = useMemo(
		() =>
			retailers.map(retailer => ({
				_id: asText(retailer.id),
				name: asText(retailer.name),
				latitude: retailer.lat,
				longitude: retailer.lng,
				category: retailer.category,
				logoUrl: retailer.logoUrl ?? null,
				monogram: asText(retailer.monogram),
				monogramColor: retailer.monogramColor ?? undefined,
			})),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[retailersKey],
	)

	return (
		<SlideMap
			camera={cameraFrom(payload?.camera)}
			basemap={basemapOf(payload)}
			fit={fit}
			label={asText(site?.name)}
			empty={site ? undefined : 'This location has no map position yet.'}
			interactive={interactive}
			onCameraChange={onCameraChange}
		>
			{map => (
				<>
					<TradeAreaRings
						map={map}
						isMapLoaded
						sites={[{ id: siteId, latitude: site.lat, longitude: site.lng }]}
						visibility={{ [siteId]: rings }}
						visible={rings.length > 0}
					/>
					{/* minZoom 0: the camera is fixed and the author asked for the
					    retailers, so they show at any framing (collision thinning
					    keeps a wide view readable). */}
					<OtherRetailersLayer map={map} isMapLoaded retailers={layerRetailers} visible={layerRetailers.length > 0} minZoom={0} />
					<LocationMarker
						map={map}
						location={{ id: siteId, assetName: asText(site.name), latitude: site.lat, longitude: site.lng }}
						color={MAP_COLORS.primary}
					/>
				</>
			)}
		</SlideMap>
	)
}

// =============================================================================
// portfolio.map
// =============================================================================

/** A plain pin for one portfolio location, in the app's primary blue. */
const portfolioLeaf = (): HTMLElement => {
	const el = document.createElement('div')
	el.style.cssText = `width:18px;height:18px;border-radius:50%;background:${MAP_COLORS.primary};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);`
	return el
}

function Note({ children }: { children: ReactNode }) {
	return <div className="pointer-events-none absolute bottom-3 left-3 z-10 rounded-md bg-white/90 px-3 py-1.5 text-[15px] text-gray-600 shadow-sm">{children}</div>
}

export function PortfolioMapBlock({ payload, interactive, onCameraChange }: MapViewProps) {
	const locations = asArray<any>(payload?.locations).filter(location => isPoint(location?.lat, location?.lng))
	const unmapped = asNumber(payload?.unmapped) ?? 0
	const cluster = payload?.cluster !== false
	const key = locations.map(location => asText(location.id)).join(',')

	const points: ClusterPoint[] = useMemo(
		() => locations.map(location => ({ id: asText(location.id), latitude: location.lat, longitude: location.lng })),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[key],
	)
	const fit = useMemo(() => {
		if (points.length === 0) return null
		if (points.length === 1) return { center: [points[0].longitude, points[0].latitude] as [number, number], zoom: 13 }
		return { bounds: tradeAreaService.getBoundsForSites(points) }
	}, [points])

	return (
		<SlideMap
			camera={cameraFrom(payload?.camera)}
			basemap={basemapOf(payload)}
			fit={fit}
			label={`${points.length} ${points.length === 1 ? 'location' : 'locations'}`}
			empty={points.length === 0 ? 'None of this portfolio\'s locations has a map position yet.' : undefined}
			overlay={unmapped > 0 ? <Note>{unmapped} not shown: no map position</Note> : undefined}
			interactive={interactive}
			onCameraChange={onCameraChange}
		>
			{map =>
				cluster ? (
					<ClusterMarkers map={map} isMapLoaded points={points} renderLeaf={portfolioLeaf} />
				) : (
					<>
						{locations.map(location => (
							<LocationMarker
								key={asText(location.id)}
								map={map}
								location={{ id: asText(location.id), assetName: asText(location.name), latitude: location.lat, longitude: location.lng }}
								color={MAP_COLORS.primary}
							/>
						))}
					</>
				)
			}
		</SlideMap>
	)
}

// =============================================================================
// evaluation.sitesMap
// =============================================================================

export function EvaluationSitesMapBlock({ payload, interactive, onCameraChange }: MapViewProps) {
	const sites = asArray<any>(payload?.sites).filter(site => isPoint(site?.lat, site?.lng))
	const rings = asArray<number>(payload?.rings).filter(ring => typeof ring === 'number' && ring > 0)
	const unmapped = asNumber(payload?.unmapped) ?? 0
	const key = `${sites.map(site => asText(site.id)).join(',')}|${rings.join(',')}`

	const fit = useMemo(() => {
		if (sites.length === 0) return null
		const points = sites.map(site => ({ longitude: site.lng, latitude: site.lat }))
		if (rings.length > 0) return { bounds: tradeAreaService.getBoundsForSitesWithRings(points, rings) }
		if (points.length === 1) return { center: [points[0].longitude, points[0].latitude] as [number, number], zoom: 13 }
		return { bounds: tradeAreaService.getBoundsForSites(points) }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key])

	// Each site keeps the colour of its position in the evaluation, as its
	// letter does, so the slide matches the evaluation's own map.
	const colorOf = (site: any) => siteColorForIndex(asNumber(site.index) ?? 0)

	const legend =
		payload?.showLegend !== false ? (
			<ul className="pointer-events-none absolute right-3 top-3 z-10 max-h-[calc(100%-24px)] space-y-1.5 overflow-hidden rounded-lg bg-white/95 px-3 py-2.5 shadow-md">
				{sites.map(site => (
					<li key={asText(site.id)} className="flex items-center gap-2 text-[16px] text-gray-800">
						<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white" style={{ background: colorOf(site) }}>
							{asText(site.letter)}
						</span>
						<span className="max-w-[220px] truncate">{asText(site.name)}</span>
					</li>
				))}
			</ul>
		) : null

	return (
		<SlideMap
			camera={cameraFrom(payload?.camera)}
			basemap={basemapOf(payload)}
			fit={fit}
			label={`${sites.length} ${sites.length === 1 ? 'site' : 'sites'}`}
			empty={sites.length === 0 ? 'None of this evaluation\'s sites has a map position yet.' : undefined}
			overlay={
				<>
					{legend}
					{unmapped > 0 && <Note>{unmapped} not shown: no map position</Note>}
				</>
			}
			interactive={interactive}
			onCameraChange={onCameraChange}
		>
			{map => (
				<>
					<TradeAreaRings
						map={map}
						isMapLoaded
						sites={sites.map(site => ({ id: asText(site.id), latitude: site.lat, longitude: site.lng }))}
						visibility={Object.fromEntries(sites.map(site => [asText(site.id), rings]))}
						visible={rings.length > 0}
					/>
					{sites.map(site => (
						<LocationMarker
							key={asText(site.id)}
							map={map}
							location={{ id: asText(site.id), assetName: asText(site.name), latitude: site.lat, longitude: site.lng }}
							color={colorOf(site)}
							label={asText(site.letter)}
						/>
					))}
				</>
			)}
		</SlideMap>
	)
}

/** The map renderers by element key, for "Frame this view". */
export const MAP_VIEWS: Record<string, (props: MapViewProps) => JSX.Element> = {
	'location.map': LocationMapBlock,
	'portfolio.map': PortfolioMapBlock,
	'evaluation.sitesMap': EvaluationSitesMapBlock,
}
